/**
 * «Collega Meta Ads» nel browser: UNA logica per il gestionale e per la console.
 *
 * ## Perché esiste (rapporto Meta v24 del 27/09/2026, punto B1 e scoperta fuori tema 2)
 * La scheda «Marketing» di un'azienda esiste in due copie gemelle: il gestionale
 * (`src/modules/admin/components/tenant-detail/TenantMarketingConfigTab.tsx`) e la console
 * (`console-app/src/components/TenantMarketingConfigTab.tsx`). Le due copie avevano già
 * divergito nel punto che conta: la console passava l'azienda alle callable, il gestionale no,
 * e «Scollega» premuto nel gestionale sulla scheda di un'azienda avrebbe agito su quella del
 * super utente. Qui sta tutto ciò che le due schede FANNO; nelle due copie resta solo ciò che
 * MOSTRANO, perché la console non ha l'i18n del gestionale e non deve trascinarselo dietro.
 *
 * ## Cosa c'è
 *  - `creaClientCollegamentoMeta(chiama, azienda)`: le callable del collegamento, con l'azienda
 *    SEMPRE passata (`tenant_id`), a ogni passo. Il server la accetta così com'è solo dal super
 *    utente (REGOLA CARDINE #4, `functions/src/callable/marketing/dialogoAccessoMeta.ts`).
 *  - `apriFinestraDialogo(win, testoAttesa)`: la finestra si apre SUBITO, dentro il clic, con un
 *    testo d'attesa. L'indirizzo del dialogo lo costruisce il server (versione dal registro,
 *    `state`), che può partire a freddo in una decina di secondi: se la finestra si aprisse dopo
 *    l'attesa della callable, il blocco dei popup del browser la fermerebbe.
 *  - `collegaMetaAds(...)`: prepara, apre, aspetta il codice, lo scambia. Restituisce gli account
 *    proposti e lo `state` con cui sceglierne uno: il token dell'utente Meta resta sul server.
 *  - `leggiMessaggioDelDialogo(...)`: accetta un messaggio solo se viene dalla NOSTRA origine,
 *    dalla finestra che abbiamo aperto noi e con lo `state` di questo dialogo.
 *  - `motivoRifiutoDelServer(err)`: il motivo che il server mette nei `details` dei suoi rifiuti,
 *    perché il gestionale lo traduca invece di mostrare la frase italiana del server.
 *
 * Niente React, niente i18n, niente SDK: la callable arriva come funzione (`chiama`), così il
 * modulo non trascina dipendenze in nessuna delle due app e si prova senza Firebase.
 */

/** La pagina a cui Meta rimanda il browser (`public/auth/facebook/callback.html`, identica in console). */
export const PERCORSO_RITORNO_META = '/auth/facebook/callback.html';

/** I messaggi che la pagina di ritorno manda alla finestra che l'ha aperta. */
export const TIPO_MESSAGGIO_CODICE_META = 'facebook_auth_code';
export const TIPO_MESSAGGIO_ERRORE_META = 'facebook_auth_error';

/** Quanto si aspetta il ritorno dal dialogo: la stessa durata dello `state` sul server. */
export const ATTESA_MASSIMA_DIALOGO_MS = 10 * 60 * 1000;

/** Dopo che la finestra risulta chiusa, quanto si aspetta ancora un messaggio già partito. */
const MARGINE_DOPO_CHIUSURA_MS = 1500;
const INTERVALLO_CONTROLLO_CHIUSURA_MS = 500;

// ============================================================================
// Le callable
// ============================================================================

/** Chiama una callable e restituisce i suoi dati (`httpsCallable(functions, nome)(dati).data`). */
export type ChiamaCallable = <T>(nome: string, dati: Record<string, unknown>) => Promise<T>;

export interface AccountMetaDaScegliere {
  id: string;
  name: string;
  /** Lo stato dell'account come lo codifica Meta (1 = attivo). */
  account_status: number;
  status_label: string;
  currency: string;
  timezone: string;
}

export interface DialogoMetaPronto {
  step: 'dialogo_pronto';
  urlDialogo: string;
  stato: string;
}

/**
 * Dopo lo scambio del codice: gli account che Meta propone, e lo `state` con cui sceglierne uno.
 * Il token dell'utente Meta NON c'è: resta sul server, cifrato, legato a questo `state`.
 */
export interface SceltaAccountMeta {
  step: 'choose_account';
  accounts: AccountMetaDaScegliere[];
  userName?: string;
  stato: string;
}

export interface CollegamentoMetaRiuscito {
  step: 'connected';
  accountId?: string;
  accountName?: string;
  pageName?: string;
}

export type EsitoCodiceMeta = SceltaAccountMeta | CollegamentoMetaRiuscito;

export interface EsitoProvaConnessione {
  success: boolean;
  account_id?: string;
  account_name?: string;
  page_name?: string;
  page_id?: string;
  currency?: string;
  timezone?: string;
  error?: string;
}

export interface ClientCollegamentoMeta {
  preparaDialogo(indirizzoRitorno: string): Promise<DialogoMetaPronto>;
  scambiaCodice(codice: string, stato: string): Promise<EsitoCodiceMeta>;
  /** `stato` è quello restituito con gli account proposti (`SceltaAccountMeta.stato`). */
  scegliAccount(idAccount: string, stato: string): Promise<EsitoCodiceMeta>;
  scollega(): Promise<void>;
  provaConnessione(piattaforma: 'meta' | 'google'): Promise<EsitoProvaConnessione>;
}

/**
 * Le callable del collegamento per UN'azienda: `tenant_id` c'è sempre, a ogni passo. È la
 * differenza che mancava al gestionale: senza, il server usava l'azienda del pass di chi chiama.
 */
export function creaClientCollegamentoMeta(chiama: ChiamaCallable, azienda: string): ClientCollegamentoMeta {
  if (!azienda) throw new Error('creaClientCollegamentoMeta: manca l’azienda');
  const conAzienda = (dati: Record<string, unknown>) => ({ ...dati, tenant_id: azienda });
  return {
    preparaDialogo: (indirizzoRitorno) =>
      chiama<DialogoMetaPronto>('facebookAdsConnect', conAzienda({ passo: 'prepara_dialogo', redirectUri: indirizzoRitorno })),
    scambiaCodice: (codice, stato) =>
      chiama<EsitoCodiceMeta>('facebookAdsConnect', conAzienda({ passo: 'scambia_codice', authorizationCode: codice, stato })),
    scegliAccount: (idAccount, stato) =>
      chiama<EsitoCodiceMeta>(
        'facebookAdsConnect',
        conAzienda({ passo: 'scegli_account', selectedAccountId: idAccount, stato }),
      ),
    scollega: async () => {
      await chiama<unknown>('facebookAdsDisconnect', conAzienda({}));
    },
    provaConnessione: (piattaforma) =>
      chiama<EsitoProvaConnessione>('testMarketingConnection', conAzienda({ platform: piattaforma })),
  };
}

// ============================================================================
// La finestra del dialogo
// ============================================================================

/** Il minimo di `window` che serve: così le prove passano una finestra finta. */
export interface FinestraOspite {
  readonly location: { readonly origin: string };
  readonly screenX: number;
  readonly screenY: number;
  readonly innerWidth: number;
  readonly innerHeight: number;
  open(url: string, nome: string, caratteristiche: string): FinestraDialogo | null;
  addEventListener(tipo: 'message', ascolta: (e: MessageEvent) => void): void;
  removeEventListener(tipo: 'message', ascolta: (e: MessageEvent) => void): void;
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number): void;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(id: number): void;
}

/** Il minimo della finestra aperta: le si cambia indirizzo, si chiude, si guarda se è chiusa. */
export interface FinestraDialogo {
  location: { href: string };
  readonly closed: boolean;
  close(): void;
  /** Il documento della finestra vuota (`about:blank`, della nostra stessa origine). */
  readonly document?: { title: string; readonly body: { textContent: string | null } | null };
}

/**
 * Apre la finestra del dialogo, centrata, con `testoAttesa` dentro (già tradotto da chi chiama):
 * mentre il server prepara il dialogo, che a freddo può volerci una decina di secondi, chi guarda
 * non vede una finestra bianca e non la chiude pensando che sia rotta.
 *
 * Va chiamata dentro il gestore del clic, prima di qualunque `await`: è l'unico momento in cui il
 * browser la lascia aprire. `null` = bloccata, anche quando il blocco dei popup restituisce una
 * finestra già chiusa (così non si chiama il server per niente).
 */
export function apriFinestraDialogo(win: FinestraOspite, testoAttesa?: string): FinestraDialogo | null {
  const larghezza = 600;
  const altezza = 700;
  const sinistra = win.screenX + (win.innerWidth - larghezza) / 2;
  const alto = win.screenY + (win.innerHeight - altezza) / 2;
  const finestra = win.open(
    'about:blank',
    'facebook_auth',
    `width=${larghezza},height=${altezza},left=${sinistra},top=${alto}`,
  );
  if (!finestra || finestra.closed) return null;
  if (testoAttesa) {
    const documento = finestra.document;
    if (documento) {
      documento.title = testoAttesa;
      if (documento.body) documento.body.textContent = testoAttesa;
    }
  }
  return finestra;
}

/** L'errore che Meta ha rimandato alla pagina di ritorno (`error`, `error_reason`, `error_description`). */
export interface ErroreDelDialogoMeta {
  errore: string | null;
  motivo: string | null;
  descrizione: string | null;
}

export type MessaggioDelDialogo = { tipo: 'codice'; codice: string } | ({ tipo: 'errore' } & ErroreDelDialogoMeta);

/** L'annullamento dell'utente su Facebook: `error=access_denied` (con `error_reason=user_denied`). */
export const ERRORE_META_ACCESSO_NEGATO = 'access_denied';

/**
 * Il messaggio viene dal NOSTRO dialogo? Serve tutto insieme: la nostra origine (la pagina di
 * ritorno sta sul nostro sito), la finestra che abbiamo aperto noi, lo `state` di questo dialogo.
 * Qualunque altra cosa (un'altra scheda, un'estensione, un dialogo vecchio) si ignora: `null`.
 */
export function leggiMessaggioDelDialogo(
  evento: { origin: string; source: unknown; data: unknown },
  atteso: { origine: string; finestra: unknown; stato: string },
): MessaggioDelDialogo | null {
  if (evento.origin !== atteso.origine) return null;
  if (evento.source !== atteso.finestra) return null;
  const dati = evento.data as Record<string, unknown> | null;
  if (!dati || typeof dati !== 'object' || dati.state !== atteso.stato) return null;
  if (dati.type === TIPO_MESSAGGIO_CODICE_META && typeof dati.code === 'string' && dati.code) {
    return { tipo: 'codice', codice: dati.code };
  }
  if (dati.type === TIPO_MESSAGGIO_ERRORE_META) {
    const testo = (v: unknown) => (typeof v === 'string' && v ? v : null);
    return {
      tipo: 'errore',
      errore: testo(dati.error),
      motivo: testo(dati.error_reason),
      descrizione: testo(dati.error_description),
    };
  }
  return null;
}

// ============================================================================
// Il collegamento, dall'inizio alla fine
// ============================================================================

export type MotivoCollegamentoInterrotto =
  | 'finestra_bloccata'
  | 'finestra_chiusa'
  /** L'utente ha annullato su Facebook (`access_denied`). */
  | 'accesso_negato'
  /** Meta ha rimandato un altro errore (configurazione dell'app, permesso non approvato…): vedi `dettaglio`. */
  | 'errore_meta'
  | 'tempo_scaduto'
  | 'interrotto'
  /**
   * Il server risponde ancora col contratto di prima del 28/09/2026: la pagina è stata pubblicata
   * prima della funzione. ⏳ Ponte temporaneo: si toglie quando `facebookAdsConnect` è pubblicata.
   */
  | 'server_da_aggiornare';

/** Il collegamento non è arrivato allo scambio del codice, per un motivo che la pagina traduce. */
export class CollegamentoMetaInterrotto extends Error {
  constructor(
    readonly motivo: MotivoCollegamentoInterrotto,
    /** Per `accesso_negato` ed `errore_meta`: ciò che Meta ha scritto (da mostrare e da registrare). */
    readonly dettaglio?: ErroreDelDialogoMeta,
  ) {
    super(
      dettaglio?.descrizione || dettaglio?.errore
        ? `Collegamento Meta interrotto: ${motivo} (${[dettaglio.errore, dettaglio.descrizione].filter(Boolean).join(': ')})`
        : `Collegamento Meta interrotto: ${motivo}`,
    );
    this.name = 'CollegamentoMetaInterrotto';
  }
}

// ============================================================================
// I rifiuti del server
// ============================================================================

/**
 * I motivi che `facebookAdsConnect` mette nei `details` dei suoi rifiuti. ⚠️ Gemello di
 * `MotivoRifiutoCollegamentoMeta` in `functions/src/callable/marketing/dialogoAccessoMeta.ts`: una
 * prova li confronta (qui non si importa dal server, per non trascinarlo nel browser).
 */
export const MOTIVI_RIFIUTO_SERVER_META = [
  'stato_mancante',
  'stato_sconosciuto',
  'stato_scaduto',
  'stato_di_altra_persona',
  'stato_di_altra_azienda',
  'pagina_da_aggiornare',
  'indirizzo_ritorno_non_valido',
  'app_non_configurata',
  'account_non_valido',
  'account_non_proposto',
] as const;

export type MotivoRifiutoServerMeta = (typeof MOTIVI_RIFIUTO_SERVER_META)[number];

/** Il motivo di un rifiuto di `facebookAdsConnect` (dai `details` dell'errore), se è uno noto. */
export function motivoRifiutoDelServer(err: unknown): MotivoRifiutoServerMeta | null {
  const dettagli = (err as { details?: unknown } | null)?.details;
  const motivo = dettagli && typeof dettagli === 'object' ? (dettagli as { motivo?: unknown }).motivo : undefined;
  return (MOTIVI_RIFIUTO_SERVER_META as readonly unknown[]).includes(motivo) ? (motivo as MotivoRifiutoServerMeta) : null;
}

/** Il rifiuto con cui la funzione di prima risponde al passo `prepara_dialogo`, che non conosce. */
const RIFIUTO_DEL_SERVER_DI_PRIMA = 'Authorization code e redirect URI obbligatori';

function eIlServerDiPrima(err: unknown): boolean {
  return err instanceof Error && err.message.includes(RIFIUTO_DEL_SERVER_DI_PRIMA);
}

function attendiCodice(
  win: FinestraOspite,
  finestra: FinestraDialogo,
  stato: string,
  segnale?: AbortSignal,
): Promise<string> {
  return new Promise<string>((risolvi, rifiuta) => {
    let chiusuraNotata: number | null = null;
    const fine = (esito: () => void) => {
      win.removeEventListener('message', ascolta);
      win.clearInterval(controllo);
      win.clearTimeout(scadenza);
      if (chiusuraNotata !== null) win.clearTimeout(chiusuraNotata);
      segnale?.removeEventListener('abort', interrompi);
      esito();
    };
    const ascolta = (evento: MessageEvent) => {
      const messaggio = leggiMessaggioDelDialogo(evento, { origine: win.location.origin, finestra, stato });
      if (!messaggio) return;
      if (messaggio.tipo === 'codice') {
        fine(() => risolvi(messaggio.codice));
        return;
      }
      const { errore, motivo, descrizione } = messaggio;
      const causa = errore === ERRORE_META_ACCESSO_NEGATO ? 'accesso_negato' : 'errore_meta';
      fine(() => rifiuta(new CollegamentoMetaInterrotto(causa, { errore, motivo, descrizione })));
    };
    const interrompi = () => fine(() => rifiuta(new CollegamentoMetaInterrotto('interrotto')));
    // La finestra chiusa a mano: si lascia un attimo a un messaggio già partito, poi si smette.
    const controllo = win.setInterval(() => {
      if (!finestra.closed || chiusuraNotata !== null) return;
      chiusuraNotata = win.setTimeout(
        () => fine(() => rifiuta(new CollegamentoMetaInterrotto('finestra_chiusa'))),
        MARGINE_DOPO_CHIUSURA_MS,
      );
    }, INTERVALLO_CONTROLLO_CHIUSURA_MS);
    const scadenza = win.setTimeout(
      () => fine(() => rifiuta(new CollegamentoMetaInterrotto('tempo_scaduto'))),
      ATTESA_MASSIMA_DIALOGO_MS,
    );
    win.addEventListener('message', ascolta);
    if (segnale?.aborted) interrompi();
    else segnale?.addEventListener('abort', interrompi);
  });
}

/**
 * Tutto il collegamento, dopo che la finestra è stata aperta nel clic (`apriFinestraDialogo`):
 * il server prepara il dialogo, la finestra ci va, il codice torna dalla pagina di ritorno e il
 * server lo scambia. Se qualcosa va storto la finestra si chiude; l'errore del server risale com'è
 * (col motivo nei `details`, vedi `motivoRifiutoDelServer`), quello del browser è un
 * `CollegamentoMetaInterrotto` col suo motivo.
 */
export async function collegaMetaAds(p: {
  client: ClientCollegamentoMeta;
  win: FinestraOspite;
  finestra: FinestraDialogo | null;
  segnale?: AbortSignal;
}): Promise<EsitoCodiceMeta> {
  const { client, win, finestra, segnale } = p;
  if (!finestra || finestra.closed) throw new CollegamentoMetaInterrotto('finestra_bloccata');
  try {
    let dialogo: DialogoMetaPronto;
    try {
      dialogo = await client.preparaDialogo(`${win.location.origin}${PERCORSO_RITORNO_META}`);
    } catch (err) {
      if (eIlServerDiPrima(err)) throw new CollegamentoMetaInterrotto('server_da_aggiornare');
      throw err;
    }
    if (!dialogo || dialogo.step !== 'dialogo_pronto' || !dialogo.urlDialogo || !dialogo.stato) {
      throw new CollegamentoMetaInterrotto('server_da_aggiornare');
    }
    if (segnale?.aborted) throw new CollegamentoMetaInterrotto('interrotto');
    if (finestra.closed) throw new CollegamentoMetaInterrotto('finestra_chiusa');
    finestra.location.href = dialogo.urlDialogo;
    const codice = await attendiCodice(win, finestra, dialogo.stato, segnale);
    return await client.scambiaCodice(codice, dialogo.stato);
  } finally {
    if (!finestra.closed) finestra.close();
  }
}

/**
 * La stessa finestra per «Meta si collega da solo» (02/11/2026, atlante cap. 470): Facebook Login for Business con la
 * configurazione dell'app. Cambiano solo le due chiamate al server (`marketingMetaCollega`), quindi si passano:
 * - `prepara(indirizzoRitorno)` → l'indirizzo della finestra e lo `state`, oppure un esito da restituire subito (il
 *   server non apre la finestra a questa azienda): allora la finestra si chiude e l'esito risale com'è;
 * - `scambia(codice, stato)` → l'esito del collegamento.
 * Tutto il resto è quello di `collegaMetaAds`: la finestra aperta dentro il clic, la pagina di ritorno, il messaggio
 * accettato solo dalla nostra origine, dalla nostra finestra e con il nostro `state`, la finestra che si chiude sempre.
 */
export async function collegaConLaFinestraDiMeta<R>(p: {
  win: FinestraOspite;
  finestra: FinestraDialogo | null;
  segnale?: AbortSignal;
  prepara: (indirizzoRitorno: string) => Promise<{ readonly pronta: true; readonly url: string; readonly stato: string } | { readonly pronta: false; readonly esito: R }>;
  scambia: (codice: string, stato: string) => Promise<R>;
}): Promise<R> {
  const { win, finestra, segnale } = p;
  if (!finestra || finestra.closed) throw new CollegamentoMetaInterrotto('finestra_bloccata');
  try {
    const preparata = await p.prepara(`${win.location.origin}${PERCORSO_RITORNO_META}`);
    if (!preparata.pronta) return preparata.esito;
    if (segnale?.aborted) throw new CollegamentoMetaInterrotto('interrotto');
    if (finestra.closed) throw new CollegamentoMetaInterrotto('finestra_chiusa');
    finestra.location.href = preparata.url;
    const codice = await attendiCodice(win, finestra, preparata.stato, segnale);
    return await p.scambia(codice, preparata.stato);
  } finally {
    if (!finestra.closed) finestra.close();
  }
}
